/** The document library.
 *
 * Everything generated across the portfolio in one place, so a charter can be found
 * without first remembering which project it belongs to.
 */

import { ExternalLink } from 'lucide-react'

import { EmptyState, ErrorState, Loading, Pill, SectionHeader } from '@/components/ui'
import { useProjects } from '@/features/projects/useProjects'
import { useDocumentLibrary } from '@/features/documents/useDocuments'
import { useKeyDocuments } from '@/features/documents/useDocuments'

const KIND_TONE = {
  key_document: 'info',
  template: 'neutral',
  attachment: 'neutral',
} as const

function ProjectDocuments({ projectId, projectName }: { projectId: string; projectName: string }) {
  const { data } = useKeyDocuments(projectId)
  const present = (data?.slots ?? []).filter((slot) => slot.present)
  const missing = (data?.slots ?? []).filter((slot) => !slot.present)

  if (present.length === 0 && missing.length === 0) return null

  return (
    <li className="px-4 py-3">
      <div className="flex flex-wrap items-center justify-between gap-2">
        <span className="font-medium text-fg">{projectName}</span>
        <span className="text-xs text-fg-muted">
          {present.length} of {present.length + missing.length} key documents
        </span>
      </div>
      {present.length > 0 ? (
        <ul className="mt-2 space-y-1">
          {present.map((slot) => (
            <li key={slot.slot} className="flex items-center gap-2 text-sm">
              <Pill tone="info">{slot.label}</Pill>
              {slot.webUrl ? (
                <a
                  href={slot.webUrl}
                  target="_blank"
                  rel="noreferrer noopener"
                  className="inline-flex items-center gap-1 text-xs underline"
                  style={{ color: 'var(--status-info)' }}
                >
                  Open <ExternalLink size={11} aria-hidden />
                </a>
              ) : null}
            </li>
          ))}
        </ul>
      ) : (
        <p className="mt-1 text-xs text-fg-muted">Nothing generated yet.</p>
      )}
    </li>
  )
}

export function DocumentsPage() {
  const { data: projectData, isPending, error } = useProjects({})
  const { data: templates } = useDocumentLibrary()

  const projects = projectData?.projects ?? []

  return (
    <div className="mx-auto max-w-[1100px] pt-2">
      <header className="mb-6">
        <h1 className="font-heading text-3xl text-fg">Documents</h1>
        <p className="mt-1 text-sm text-fg-muted">
          Key documents live with their project and open in Word. Generate them from the project
          drawer, where they are prefilled from what the project already knows.
        </p>
      </header>

      <SectionHeader>By project</SectionHeader>
      {isPending ? (
        <Loading label="Loading documents" />
      ) : error ? (
        <ErrorState error={error} />
      ) : projects.length === 0 ? (
        <EmptyState title="No projects yet">
          Documents are generated per project, so there is nothing to show until one exists.
        </EmptyState>
      ) : (
        <ul className="card divide-y divide-border">
          {projects.map((project) => (
            <ProjectDocuments key={project.id} projectId={project.id} projectName={project.name} />
          ))}
        </ul>
      )}

      <div className="mt-8">
        <SectionHeader>Organisation templates</SectionHeader>
      </div>
      {(templates?.documents ?? []).length === 0 ? (
        <EmptyState title="No shared templates">
          Key documents are generated per project rather than copied from a master, so a shared
          template is rarely needed. Anything stored here applies across the portfolio.
        </EmptyState>
      ) : (
        <ul className="card divide-y divide-border">
          {(templates?.documents ?? []).map((document) => (
            <li key={document.id} className="flex items-center justify-between px-4 py-3">
              <div className="flex items-center gap-2">
                <span className="text-fg">{document.title}</span>
                <Pill tone={KIND_TONE[document.kind as keyof typeof KIND_TONE] ?? 'neutral'}>
                  {document.kind.replace('_', ' ')}
                </Pill>
              </div>
              {document.webUrl ? (
                <a
                  href={document.webUrl}
                  target="_blank"
                  rel="noreferrer noopener"
                  className="inline-flex items-center gap-1 text-sm underline"
                  style={{ color: 'var(--status-info)' }}
                >
                  Open <ExternalLink size={12} aria-hidden />
                </a>
              ) : null}
            </li>
          ))}
        </ul>
      )}
    </div>
  )
}
